import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client.js";
import type { ClusterState } from "../api/telemetry.js";
import type { Cluster, DeviceProbe } from "../components/ClusterCard.js";
import { resolveStatus } from "../components/ClusterCard.js";
import { AlarmBanner, type AlarmRow } from "../components/AlarmBanner.js";
import { Icon } from "../components/Icon.js";
import { LaserMarker } from "../components/LaserMarker.js";
import { PlantView } from "../components/PlantView.js";
import { PtzPanel } from "../components/PtzPanel.js";
import { VideoPlayer } from "../components/VideoPlayer.js";
import { useI18n } from "../i18n.js";
import { usePtzSmooth, type Waypoint } from "../ptz-geometry.js";
import { useSession } from "../session.js";

/**
 * Un impianto solo, in grande. Portata da `FocusMode.qml`.
 *
 * La forma e' quella dell'app desktop, e non e' arbitraria:
 *
 *  - il **video occupa tutto**, con i comandi sopra in trasparenza — non
 *    dentro un riquadro con i bordi. Chi sorveglia guarda l'immagine, e ogni
 *    pixel tolto al video e' dato tolto a lui.
 *  - il pannello con la lettura sta **sotto** il video, non in overlay: cosi'
 *    il fotogramma resta intero.
 *  - la concentrazione e' un numero **grande**, leggibile dall'altra parte
 *    della stanza. E' l'unica cifra che conta su questa schermata.
 *  - la colonna PTZ e' a destra, larga il 26% e mai meno di 300 px.
 */

type ViewMode = "video" | "plant" | "ir";

interface Reading {
  id: number | string;
  timestamp: string;
  ppm: number | null;
}

interface SystemLogRow {
  id: number;
  ts: string;
  category: string;
  level: string;
  msgKey: string;
  msgParam: string | null;
}

/** Una riga del registro sotto il video: allarmi, letture sopra soglia e
 *  eventi di sistema sulla stessa linea del tempo. */
type Entry =
  | { kind: "alarm"; ts: string; row: AlarmRow }
  | { kind: "ppm"; ts: string; ppm: number | null }
  | { kind: "system"; ts: string; row: SystemLogRow };

/** Ogni quanto si rileggono le letture per il grafico. Il ciclo del server
 *  campiona molto piu' spesso; ridisegnare a quel ritmo non aggiungerebbe
 *  informazione e terrebbe occupata la scheda video. */
const READINGS_POLL_MS = 10_000;

export function FocusMode({
  cluster,
  telemetry,
  probe,
  alarms,
  onAlarmsChanged,
  onBack,
  onLogs,
}: {
  cluster: Cluster;
  telemetry?: ClusterState | undefined;
  probe?: DeviceProbe | undefined;
  /** Gli allarmi di tutti gli impianti: li tiene la sessione, che li rilegge
   *  per la campanella. Chiederli di nuovo qui darebbe due elenchi che
   *  possono divergere di qualche secondo. */
  alarms: AlarmRow[];
  onAlarmsChanged: () => void;
  onBack: () => void;
  onLogs: () => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();

  const [view, setView] = useState<ViewMode>("video");
  const [streams, setStreams] = useState<Partial<Record<"video" | "ir", string>>>({});
  const [playing, setPlaying] = useState(false);
  const [readings, setReadings] = useState<Reading[]>([]);
  const [uptime, setUptime] = useState<number | undefined>();
  const [systemLog, setSystemLog] = useState<SystemLogRow[]>([]);
  // Gli allarmi si possono disarmare per impianto. Il riquadro rosso non deve
  // comparire su un sistema disarmato: prometterebbe una sorveglianza che non
  // c'e'.
  const [alarmsEnabled, setAlarmsEnabled] = useState(true);
  const [error, setError] = useState("");
  /** Alza il contatore per far ripartire subito il giro di letture dopo che
   *  l'operatore ha confermato: aspettare i dieci secondi del ciclo
   *  lascerebbe il riquadro rosso acceso su un allarme gia' confermato. */
  const [refresh, setRefresh] = useState(0);

  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);

  const l1 = cluster.thresholdL1 ?? 0;
  const l2 = cluster.thresholdL2 ?? 0;

  /** Gli allarmi di QUESTO impianto, i piu' recenti. */
  const clusterAlarms = useMemo(
    () => alarms.filter((a) => a.clusterId === cluster.id).slice(0, 12),
    [alarms, cluster.id],
  );

  /**
   * Il registro sotto il video: allarmi, letture **sopra la soglia di avviso**
   * ed eventi di sistema, sulla stessa linea del tempo.
   *
   * Le letture sotto soglia restano fuori: sono migliaia, e seppellirebbero
   * le altre due fonti proprio quando servono. Le tre insieme sono quello che
   * si guarda per capire cosa e' successo in quest'ora, ed e' il motivo per
   * cui l'app desktop le unisce invece di tenerle in tre elenchi.
   */
  const events = useMemo<Entry[]>(() => {
    const out: Entry[] = [
      ...clusterAlarms.map((row) => ({ kind: "alarm", ts: row.createdAt, row }) as const),
      ...readings
        .filter((r) => Number(r.ppm) >= l1)
        .map((r) => ({ kind: "ppm", ts: r.timestamp, ppm: r.ppm }) as const),
      ...systemLog.map((row) => ({ kind: "system", ts: row.ts, row }) as const),
    ];
    return out.sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts)).slice(0, 12);
  }, [clusterAlarms, readings, systemLog, l1]);


  const status = resolveStatus(cluster, telemetry, probe);
  const livePpm = telemetry?.ppm ?? cluster.currentPpm ?? undefined;
  const flowRate = computeFlowRate(livePpm, telemetry?.windSpeed, telemetry?.windDirection, telemetry?.ptzAngle);

  // La posizione della testa, interpolata fra un campione e il successivo.
  // Ci vive dentro un ref: la usa il fascio sulla pianta, che si ridisegna da
  // solo a 60 fps.
  const ptz = usePtzSmooth(telemetry);

  // I waypoint della traccia attiva servono a una cosa sola: capire se la
  // ronda e' un giro completo o un ventaglio, perche' il fascio parte dal
  // centro della pianta nel primo caso e dal sensore nel secondo.
  useEffect(() => {
    const traceId = cluster.activePtzTraceId;
    if (!traceId) {
      setWaypoints([]);
      return;
    }
    let cancelled = false;
    void api
      .call<{ waypoints?: Waypoint[] }>("ptz-traces.get", { id: traceId })
      .then((r) => {
        if (!cancelled) setWaypoints(r.waypoints ?? []);
      })
      .catch(() => {
        // Traccia sparita o non leggibile: il fascio ripiega sul ventaglio,
        // che e' la geometria calibrata sull'impianto.
        if (!cancelled) setWaypoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster.activePtzTraceId]);

  const onPlayingChange = useCallback((v: boolean) => setPlaying(v), []);

  // Quali flussi esistono lo sa il server, che conosce la configurazione del
  // ritrasmettitore. La scheda IR compare solo se quella camera c'e'.
  useEffect(() => {
    let cancelled = false;
    const load = (kind: "video" | "ir", route: "clusters.rtspUrl" | "clusters.irRtspUrl") =>
      api
        .call<{ hls: string }>(route, { id: cluster.id })
        .then((r) => {
          if (!cancelled) setStreams((s) => ({ ...s, [kind]: r.hls }));
        })
        .catch(() => {
          /* 404 = camera non configurata, 503 = ritrasmettitore assente */
        });
    void load("video", "clusters.rtspUrl");
    void load("ir", "clusters.irRtspUrl");
    return () => {
      cancelled = true;
    };
  }, [api, cluster.id]);

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const since = new Date(Date.now() - 3600_000).toISOString();
        const [r, en, sys, hea] = await Promise.all([
          api.call<Reading[]>("emissions.readings", { clusterId: cluster.id, since, limit: 240 }),
          api
            .call<{ enabled?: boolean }>("alarms.enabled", {})
            .catch(() => ({ enabled: true })),
          api
            .call<SystemLogRow[]>("system-log.findByCluster", { clusterId: cluster.id, limit: 40 })
            .catch(() => [] as SystemLogRow[]),
          // Un 403 qui non e' un guasto: e' un operatore senza `accessSettings`.
          // Il tempo di funzionamento sparisce dalla barra e basta.
          api
            .call<{ uptime?: number }>("settings.health")
            .catch((): { uptime?: number } => ({})),
        ]);
        if (stop) return;
        setReadings([...r].reverse());
        setSystemLog(sys);
        setUptime(typeof hea.uptime === "number" ? hea.uptime : undefined);
        setAlarmsEnabled(en.enabled !== false);
        setError("");
      } catch (e) {
        if (!stop && e instanceof ApiError && !e.isForbidden) setError(e.message);
      }
      if (!stop) timer = setTimeout(() => void tick(), READINGS_POLL_MS);
    };
    void tick();
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
    };
  }, [api, cluster.id, refresh]);

  const hasIr = streams.ir !== undefined;
  const src = view === "ir" ? streams.ir : streams.video;

  // Il marker si puo' spegnere per impianto; senza una camera configurata non
  // ha niente su cui posarsi.
  const laserMarkerOn =
    Boolean(cluster.cameraIp) &&
    (cluster.laserVideoMarkerEnabled === undefined ||
      cluster.laserVideoMarkerEnabled === null ||
      cluster.laserVideoMarkerEnabled === true ||
      cluster.laserVideoMarkerEnabled === 1);

  // Togliere la camera IR dalla configurazione mentre la si guarda lascerebbe
  // una sorgente vuota senza spiegazione: si torna al video.
  useEffect(() => {
    if (view === "ir" && !hasIr) setView("video");
  }, [view, hasIr]);

  return (
    <div className="focus">
      <div className="focus__main">
        {/* ── palco video: l'immagine occupa tutto, i comandi ci stanno
             sopra in trasparenza ─────────────────────────────────────── */}
        <div className="stage">
          {view === "plant" ? (
            <PlantView
              cluster={cluster}
              telemetry={telemetry}
              livePpm={livePpm}
              ptz={ptz}
              waypoints={waypoints}
            />
          ) : (
            <VideoPlayer src={src} onPlayingChange={onPlayingChange}>
              {/* Il marker sta solo sulla vista VIDEO. Sull'ottica IR
                  puntrebbe nel posto sbagliato: la sua geometria e' calcolata
                  sul campo visivo della Hikvision e sulla sua distanza dal
                  laser, non su quelle del sensore infrarosso. */}
              {view === "video" && laserMarkerOn && <LaserMarker cluster={cluster} ptz={ptz} />}
            </VideoPlayer>
          )}

          <div className="stage__gradient" />

          <button className="stage__back" onClick={onBack} title={t("nav.gridView")}>
            <Icon name="arrow-left" size={20} />
          </button>

          <div className="stage__id">
            <div className="stage__idRow">
              <h2>{cluster.name}</h2>
              <span className={`badge badge--${status.toLowerCase()}`}>{status}</span>
            </div>
            {/* Solo sigla d'area e nome della vista, come il .qml: la
                posizione della testa sta nella colonna PTZ, dove la si guarda
                mentre la si muove, non quassu' sopra il video. */}
            <div className="stage__area">
              <Icon name="activity" size={14} />
              {cluster.areaCode ?? ""}
              {view === "plant" && `  |  ${t("focus.clusterPlant")}`}
            </div>
          </div>

          {/* Ogni scheda ha la sua icona e l'ordine e' quello del .qml —
              video, infrarosso, pianta — perche' e' l'ordine in cui si
              passa da una all'altra guardando la stessa scena. */}
          <div className="stage__modes">
            <button
              className={`vm${view === "video" ? " vm--on" : ""}`}
              onClick={() => setView("video")}
            >
              <Icon name="video" size={14} />
              {t("focus.video")}
            </button>
            {/* La scheda IR c'e' solo se quella camera esiste, e i due flussi
                sono ALTERNATIVI: mai due decodificatori accesi insieme. */}
            {hasIr && (
              <button className={`vm${view === "ir" ? " vm--on" : ""}`} onClick={() => setView("ir")}>
                <Icon name="zap" size={14} />
                {t("focus.ir")}
              </button>
            )}
            <button
              className={`vm${view === "plant" ? " vm--on" : ""}`}
              onClick={() => setView("plant")}
            >
              <Icon name="map" size={14} />
              {t("focus.plant")}
            </button>
          </div>

          <AlarmBanner
            cluster={cluster}
            alarms={clusterAlarms}
            status={status}
            livePpm={livePpm}
            alarmsEnabled={alarmsEnabled}
            onChanged={() => {
              onAlarmsChanged();
              setRefresh((n) => n + 1);
            }}
          />

          {view !== "plant" && (
            <span className="stage__live">
              <i className={playing ? "dot dot--live" : "dot"} />
              {playing ? t("focus.live") : t("focus.offline")}
            </span>
          )}
        </div>

        {/* ── pannello sotto il video: lettura, andamento, eventi, stato ── */}
        <div className="hud">
          {error && <p className="alert">{error}</p>}

          <div className="hud__top">
            <div className="hud__ppm">
              <div className="hud__ppmLabel">{t("focus.realtimeConcentration")}</div>
              <div className={`hud__ppmValue hud__ppmValue--${status.toLowerCase()}`}>
                {status === "OFFLINE" || livePpm === undefined ? "--" : Math.round(livePpm)}
              </div>
              <div className="hud__ppmUnit">PPM·M</div>
              {/* La portata: quanto sta uscendo, non solo che c'e' del gas.
                  Compare solo quando l'anemometro risponde — senza vento il
                  numero non si puo' calcolare, e stamparne uno finto su una
                  schermata di sorveglianza sarebbe peggio che non averlo. */}
              {flowRate !== undefined && (
                <div className="hud__flow">
                  <span className="hud__flowValue">{flowRate.toFixed(1)}</span>
                  <span className="hud__flowUnit">g/h</span>
                </div>
              )}
            </div>

            <Trend readings={readings} l1={l1} l2={l2} />
          </div>

          <div className="hud__events">
            <div className="hud__title">
              <Icon name="file-text" size={12} />
              {t("hdr.eventLog")}
              <span className="muted small">
                (L1={Math.round(l1)} L2={Math.round(l2)})
              </span>
            </div>
            {/* Il titolo resta fuori e la tabella sta in un riquadro suo —
                fondo nero al 40%, un filo di bordo chiaro, angolo da 8 — come
                nel .qml. Senza cornice le righe galleggiavano sul fondo della
                pagina e il registro non si leggeva come un blocco. */}
            <div className="hud__eventsBox">
            <table className="table table--events">
              <thead>
                <tr>
                  <th>{t("focus.eventDate")}</th>
                  <th>{t("focus.eventTime")}</th>
                  <th>TYPE</th>
                  <th>DETAILS</th>
                  <th className="ta-right">PPM·M</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => {
                  const d = new Date(e.ts);
                  const bad = Number.isNaN(d.getTime());
                  const critical = e.kind === "alarm" && e.row.level === "L2";
                  const tone =
                    e.kind === "alarm"
                      ? critical
                        ? "critical"
                        : "warning"
                      : e.kind === "system"
                        ? e.row.level === "WARNING"
                          ? "warning"
                          : "sys"
                        : "ppm";
                  const badge =
                    e.kind === "alarm"
                      ? (e.row.level ?? "ALARM")
                      : e.kind === "system"
                        ? e.row.category || "SYS"
                        : "PPM";
                  const details =
                    e.kind === "alarm"
                      ? `${e.row.state ?? ""}${e.row.priority ? `  •  ${e.row.priority}` : ""}`
                      : e.kind === "system"
                        ? t(e.row.msgKey, { p: e.row.msgParam ?? "" })
                        : t("focus.sensorReading");
                  const ppm =
                    e.kind === "alarm" ? e.row.ppmValue : e.kind === "ppm" ? e.ppm : null;
                  return (
                    <tr key={`${e.kind}-${e.ts}-${i}`}>
                      <td className="muted mono">{bad ? "—" : d.toLocaleDateString("en-GB")}</td>
                      <td className="muted mono">
                        {bad ? "—" : d.toLocaleTimeString("en-GB", { hour12: false })}
                      </td>
                      <td>
                        <span className={`logbadge logbadge--${tone}`}>{badge}</span>
                      </td>
                      <td>{details}</td>
                      <td className="ta-right mono">
                        {ppm === null || ppm === undefined ? "—" : Number(ppm).toFixed(1)}
                      </td>
                    </tr>
                  );
                })}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      {t("focus.noAlarmEvents")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* La barra di stato dell'app desktop: ogni apparato con il proprio
              valore, e "N/C" quando non e' configurato — che e' diverso da
              offline. */}
          <div className="statusbar">
            {/* Le voci sono quelle del .qml e nello stesso ordine, separate da
                una barretta: stato, sensore, telecamera PTZ, (vento), DCS.
                La voce PTZ a se' stante e' stata tolta — nell'originale la
                telecamera PTZ e' una sola. */}
            <SChip
              label={t("focus.systemStatus")}
              value={status}
              tone={status.toLowerCase()}
              pulse={status === "WARNING" || status === "CRITICAL"}
            />
            <SDvd />
            <SChip
              label={t("focus.laserSensor")}
              value={deviceLabel(probe?.sensor, t)}
              tone={tone(probe?.sensor)}
            />
            <SDvd />
            <SChip
              label={t("focus.ptzCamera")}
              value={deviceLabel(probe?.camera, t)}
              tone={tone(probe?.camera)}
            />
            {/* L'anemometro compare solo dove esiste. */}
            {Boolean(cluster.windEnabled) && (
              <>
                <SDvd />
                <SChip
                  label="Wind"
                  value={
                    telemetry?.windSpeed !== undefined
                      ? `${telemetry.windSpeed.toFixed(1)} m/s`
                      : cluster.windSensorIp
                        ? t("focus.offline")
                        : "N/C"
                  }
                  tone={telemetry?.windOnline ? "normal" : "muted"}
                />
              </>
            )}
            <SDvd />
            <SChip
              label="DCS"
              value={
                telemetry?.moxaOnline ? t("focus.online") : cluster.moxaIp ? t("focus.offline") : "N/C"
              }
              tone={telemetry?.moxaOnline ? "normal" : "muted"}
            />
            <SDvd />
            {/* Il registro eventi: e' da qui che ci si arriva nell'app
                desktop, non da una scheda della configurazione. Si guarda
                quando bisogna ricostruire cosa e' successo, e a quel punto si
                sta guardando questo impianto. */}
            <button className="statusbar__logs" onClick={onLogs}>
              LOGS
            </button>
            <span className="statusbar__spacer" />
            {/* Il tempo di funzionamento e' quello del SERVIZIO, non della
                scheda del browser: lo legge `settings.health`. Chi non ha
                `accessSettings` non lo vede — sul desktop lo vedono tutti,
                perche' li' e' l'uptime del programma che si ha davanti. */}
            {uptime !== undefined && (
              <span className="statusbar__uptime">
                <Icon name="timer" size={12} />
                {t("focus.operationTime")}:
                <b className="statusbar__uptimeValue">{formatUptime(uptime)}</b>
              </span>
            )}
            <span className="muted small">{cluster.name}</span>
          </div>
        </div>
      </div>

      <PtzPanel cluster={cluster} telemetry={telemetry} livePpm={livePpm} />
    </div>
  );
}

function deviceLabel(state: string | undefined, t: (k: string) => string): string {
  // "N/C" e OFFLINE sono due cose diverse: uno non e' configurato, l'altro non
  // risponde. Confonderli manda a cercare un guasto che non c'e'.
  if (state === undefined) return "…";
  if (state === "unconfigured") return "N/C";
  return state === "online" ? t("focus.online") : t("focus.offline");
}

function tone(state: string | undefined): string {
  if (state === "online") return "normal";
  if (state === "offline") return "critical";
  return "muted";
}

function SChip({
  label,
  value,
  tone,
  pulse,
}: {
  label: string;
  value: string;
  tone: string;
  pulse?: boolean;
}) {
  return (
    <span className="schip">
      <span className="schip__label">{label}:</span>
      {/* Il pallino prima del valore: da lontano la barra si legge come una
          fila di luci, e le parole si leggono solo se serve il dettaglio.
          Pulsa quando lo stato e' in allarme, come nel .qml. */}
      <i className={`schip__dot schip__dot--${tone}${pulse ? " schip__dot--pulse" : ""}`} />
      <span className={`schip__value schip__value--${tone}`}>{value}</span>
    </span>
  );
}

/** La barretta fra un gruppo e l'altro (`SDvd` nel .qml). */
function SDvd() {
  return <span className="schip__dvd">|</span>;
}

/** Stesso formato del `.qml`: ore, minuti, secondi, con i giorni davanti
 *  quando il servizio e' su da piu' di uno. */
function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const p = (n: number) => String(Math.floor(n)).padStart(2, "0");
  const hms = `${p((seconds % 86400) / 3600)}:${p((seconds % 3600) / 60)}:${p(seconds % 60)}`;
  return d > 0 ? `${d}d ${hms}` : hms;
}

/**
 * L'andamento dell'ultima ora, con le bande delle due soglie.
 *
 * Le bande sono lo sfondo, non una linea sopra il grafico: cosi' si legge in
 * quale zona sta la curva senza confrontarla con niente. Un grafico senza le
 * soglie disegnate costringe a ricordarsele, e davanti a un allarme nessuno se
 * le ricorda.
 */
/**
 * La portata della perdita, in grammi all'ora.
 *
 * Porting di `emissionCalc.computeFlowRate` (Op. 11), la stessa formula che
 * `FocusMode.qml` calcola dal vivo: la concentrazione integrata sul cammino
 * diventa massa per unita' di area, il vento la porta via, e quanto ne passa
 * dipende da quanto il fascio taglia il pennacchio di traverso.
 *
 * Torna `undefined` — e la riga sparisce — quando manca il vento o la
 * concentrazione e' nulla: non e' zero, e' "non lo so". Il seno ha un
 * pavimento a 0,1 perche' con vento parallelo al fascio la formula
 * divergerebbe verso lo zero e direbbe che non esce niente.
 */
function computeFlowRate(
  ppm: number | undefined,
  windSpeed: number | undefined,
  windDirection: number | undefined,
  ptzAngle: number | undefined,
): number | undefined {
  if (ppm === undefined || ppm <= 0) return undefined;
  if (windSpeed === undefined || windSpeed <= 0.1) return undefined;
  const CH4_MOLAR_MASS = 16.04;
  const MOLAR_VOLUME = 24.45;
  const surface = ppm * 1e-6 * (CH4_MOLAR_MASS / MOLAR_VOLUME);
  const theta = Math.abs((ptzAngle ?? 0) - (windDirection ?? 0));
  const sinT = Math.max(0.1, Math.abs(Math.sin(((theta % 180) * Math.PI) / 180)));
  return surface * windSpeed * sinT * 3600;
}

function Trend({ readings, l1, l2 }: { readings: Reading[]; l1: number; l2: number }) {
  const { t } = useI18n();
  const W = 900;
  const H = 120;

  const model = useMemo(() => {
    const pts = readings
      .map((r) => ({ v: r.ppm, t: Date.parse(r.timestamp) }))
      .filter((p): p is { v: number; t: number } => typeof p.v === "number" && Number.isFinite(p.v));
    if (pts.length < 2) return undefined;

    // La scala verticale arriva sempre almeno alla soglia L2: senza, una
    // giornata tranquilla disegnerebbe il rumore a tutta altezza e sembrerebbe
    // una crisi. Il 18% di margine sopra il massimo e' quello del .qml.
    const peak = Math.max(l2, ...pts.map((p) => p.v));
    const max = peak + peak * 0.18 + 10;
    const step = W / (pts.length - 1);
    const x = (i: number) => i * step;
    const y = (v: number) => H - (v / max) * H;

    // Curva liscia, non spezzata: Catmull-Rom convertita in Bezier cubiche,
    // come il .qml. Su una lettura che cambia di poco a ogni campione la
    // spezzata disegna denti che non ci sono nel gas.
    const p = pts.map((pt, i) => ({ x: x(i), y: y(pt.v) }));
    let line = `M${p[0]!.x.toFixed(1)},${p[0]!.y.toFixed(1)}`;
    for (let i = 0; i < p.length - 1; i++) {
      const p0 = p[i - 1] ?? p[i]!;
      const p1 = p[i]!;
      const p2 = p[i + 1]!;
      const p3 = p[i + 2] ?? p2;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      line += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
    }
    const area = `${line} L${W},${H} L0,${H} Z`;

    // Etichette dell'asse dei tempi, ricavate dai campioni veri e non da una
    // finestra dichiarata: se il sensore ha smesso di rispondere mezz'ora fa,
    // il grafico deve dirlo invece di fingere di arrivare a "ora".
    const span = pts[pts.length - 1]!.t - pts[0]!.t;
    const xLabels = [0, 0.25, 0.5, 0.75, 1].map((f) => {
      const backMs = span * (1 - f);
      const mins = Math.round(backMs / 60000);
      return { at: f, label: mins === 0 ? "now" : `-${mins}m` };
    });

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ at: f, v: max * f }));

    const last = p[p.length - 1]!;
    return { line, area, max, y, yTicks, xLabels, last };
  }, [readings, l1, l2, t]);

  const fmt = (v: number) => {
    if (v >= 10000) return `${Math.round(v / 1000)}k`;
    if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;
    return String(Math.round(v));
  };

  return (
    <div className="trend">
      {model ? (
        <div className="trend__plot">
          {/* Scala verticale: le tacche stanno fuori dall'area disegnata, in
              HTML, perche' l'SVG e' stirato in larghezza e il testo dentro
              verrebbe schiacciato con lui. */}
          {model.yTicks.map((tick) => (
            <span
              key={tick.at}
              className="trend__ytick"
              style={{ bottom: `${tick.at * 100}%` }}
            >
              {fmt(tick.v)}
            </span>
          ))}

          <div className="trend__box">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="trend__svg">
              <defs>
                {/* L'area sotto la curva e' tagliata in tre fasce: verde sotto
                    L1, ambra fra L1 e L2, rossa oltre. Il colore dice a che
                    altezza stava la lettura senza dover leggere la scala. */}
                <clipPath id="tz-normal">
                  <rect x="0" y={l1 > 0 ? model.y(l1) : 0} width={W} height={H} />
                </clipPath>
                <clipPath id="tz-warning">
                  <rect
                    x="0"
                    y={l2 > 0 ? model.y(l2) : 0}
                    width={W}
                    height={l1 > 0 ? Math.max(0, model.y(l1) - (l2 > 0 ? model.y(l2) : 0)) : 0}
                  />
                </clipPath>
                <clipPath id="tz-critical">
                  <rect x="0" y="0" width={W} height={l2 > 0 ? model.y(l2) : 0} />
                </clipPath>
              </defs>

              {/* Bande di fondo */}
              <rect x="0" y="0" width={W} height={H} fill="var(--status-normal-bg05)" />
              {l1 > 0 && (
                <rect x="0" y="0" width={W} height={model.y(l1)} fill="var(--status-warning-bg05)" />
              )}
              {l2 > 0 && (
                <rect x="0" y="0" width={W} height={model.y(l2)} fill="var(--status-critical-bg10)" />
              )}

              {/* Griglia orizzontale sulle tacche intermedie */}
              {model.yTicks.slice(1, -1).map((tick) => (
                <line
                  key={tick.at}
                  x1="0"
                  x2={W}
                  y1={H - tick.at * H}
                  y2={H - tick.at * H}
                  stroke="rgba(255,255,255,0.04)"
                  strokeWidth="1"
                />
              ))}

              {/* Soglie */}
              {l1 > 0 && (
                <line
                  x1="0"
                  x2={W}
                  y1={model.y(l1)}
                  y2={model.y(l1)}
                  stroke="var(--status-normal)"
                  strokeWidth="1"
                  opacity="0.45"
                />
              )}
              {l2 > 0 && (
                <line
                  x1="0"
                  x2={W}
                  y1={model.y(l2)}
                  y2={model.y(l2)}
                  stroke="var(--status-critical)"
                  strokeWidth="1"
                  opacity="0.45"
                />
              )}

              <path d={model.area} fill="var(--status-normal-bg20)" clipPath="url(#tz-normal)" />
              <path d={model.area} fill="var(--status-warning-bg20)" clipPath="url(#tz-warning)" />
              <path d={model.area} fill="var(--status-critical-bg20)" clipPath="url(#tz-critical)" />

              <path
                d={model.line}
                fill="none"
                stroke="var(--status-normal)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* L'ultimo campione, con l'alone. Fermo significa fermo: senza
                questo punto una curva che non si aggiorna piu' e' identica a
                una che sta leggendo un valore stabile. */}
            <span
              className="trend__last"
              style={{ left: "100%", top: `${(model.last.y / H) * 100}%` }}
            />
          </div>

          {/* Le due soglie con il loro valore, a destra del disegno: sono
              i numeri contro cui si legge la curva, e nel .qml stanno li'
              invece che dentro il grafico dove coprirebbero la traccia. */}
          <div className="trend__legend">
            <span className="trend__legendItem trend__legendItem--l2">
              <i />L2: {Math.round(l2)}
            </span>
            <span className="trend__legendItem trend__legendItem--l1">
              <i />L1: {Math.round(l1)}
            </span>
          </div>

          <div className="trend__xaxis">
            {model.xLabels.map((lab) => (
              <span key={lab.at} className="trend__xtick" style={{ left: `${lab.at * 100}%` }}>
                {lab.label}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="trend__empty">{t("msg.noEmissionPeriod")}</div>
      )}
    </div>
  );
}

